import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import './HomeSections.css';

// =============================================================================
// SVG ICONS
// =============================================================================

const CalendarIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="18" x="3" y="4" rx="2" ry="2"/>
        <line x1="16" x2="16" y1="2" y2="6"/>
        <line x1="8" x2="8" y1="2" y2="6"/>
        <line x1="3" x2="21" y1="10" y2="10"/>
        <path d="m9 16 2 2 4-4"/>
    </svg>
);

const EditIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/>
        <path d="m15 5 4 4"/>
    </svg>
);

const BookmarkIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/>
    </svg>
);

const SearchIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.3-4.3"/>
    </svg>
);

const UnlockIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
    </svg>
);

const SparklesIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
        <path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
    </svg>
);

const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const ArrowRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7"/>
    </svg>
);

// =============================================================================
// WHAT YOU GET SECTION
// =============================================================================

export function WhatYouGetSection() {
    const { t } = useTranslation();

    const cards = [
        {
            icon: <CalendarIcon />,
            title: t('home.whatYouGet.plan.title'),
            desc: t('home.whatYouGet.plan.desc'),
            proof: t('home.whatYouGet.plan.proof'),
        },
        {
            icon: <EditIcon />,
            title: t('home.whatYouGet.edit.title'),
            desc: t('home.whatYouGet.edit.desc'),
            proof: t('home.whatYouGet.edit.proof'),
        },
        {
            icon: <BookmarkIcon />,
            title: t('home.whatYouGet.book.title'),
            desc: t('home.whatYouGet.book.desc'),
            proof: t('home.whatYouGet.book.proof'),
        },
    ];

    return (
        <section className="what-you-get-section section-container">
            <div className="section-inner">
                <h2 className="section-title">{t('home.whatYouGet.title')}</h2>
                <div className="what-you-get-grid">
                    {cards.map((card, i) => (
                        <div key={i} className="what-you-get-card">
                            <div className="what-you-get-icon">{card.icon}</div>
                            <h3 className="what-you-get-title">{card.title}</h3>
                            <p className="what-you-get-desc">{card.desc}</p>
                            <span className="what-you-get-proof">{card.proof}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// =============================================================================
// HOW IT WORKS SECTION
// =============================================================================

export function HowItWorksSection() {
    const { t } = useTranslation();

    const steps = [
        {
            number: '1',
            icon: <SearchIcon />,
            title: t('home.howItWorks.step1.title'),
            desc: t('home.howItWorks.step1.desc'),
        },
        {
            number: '2',
            icon: <UnlockIcon />,
            title: t('home.howItWorks.step2.title'),
            desc: t('home.howItWorks.step2.desc'),
        },
        {
            number: '3',
            icon: <SparklesIcon />,
            title: t('home.howItWorks.step3.title'),
            desc: t('home.howItWorks.step3.desc'),
        },
    ];

    return (
        <section className="how-it-works-section section-container">
            <div className="section-inner">
                <h2 className="section-title">{t('home.howItWorks.title')}</h2>
                <div className="how-it-works-grid">
                    {steps.map((step, i) => (
                        <div key={i} className="how-it-works-step">
                            <div className="step-number">{step.number}</div>
                            <div className="step-icon">{step.icon}</div>
                            <h3 className="step-title">{step.title}</h3>
                            <p className="step-desc">{step.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// =============================================================================
// PRICING SECTION
// =============================================================================

interface PricingSectionProps {
    onOpenModal?: () => void;
}

export function PricingSection({ onOpenModal }: PricingSectionProps) {
    const { t } = useTranslation();

    const freeFeatures = [
        t('home.pricing.freeFeatures.f1'),
        t('home.pricing.freeFeatures.f2'),
        t('home.pricing.freeFeatures.f3'),
    ];

    const paidFeatures = [
        t('home.pricing.paidFeatures.f1'),
        t('home.pricing.paidFeatures.f2'),
        t('home.pricing.paidFeatures.f3'),
    ];

    return (
        <section className="pricing-section section-container">
            <div className="section-inner">
                <h2 className="section-title">{t('home.pricing.title')}</h2>
                <div className="pricing-grid">
                    {/* Free Column */}
                    <div className="pricing-card pricing-free">
                        <div className="pricing-icon pricing-icon-free">
                            <SearchIcon />
                        </div>
                        <h3 className="pricing-tier">{t('home.pricing.free')}</h3>
                        <ul className="pricing-features">
                            {freeFeatures.map((feature, i) => (
                                <li key={i}>
                                    <CheckIcon />
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    
                    {/* Paid Column */}
                    <div className="pricing-card pricing-paid">
                        <span className="pricing-badge">{t('home.pricing.paidBadge')}</span>
                        <div className="pricing-icon pricing-icon-paid">
                            <SparklesIcon />
                        </div>
                        <h3 className="pricing-tier">{t('home.pricing.paid')}</h3>
                        <ul className="pricing-features">
                            {paidFeatures.map((feature, i) => (
                                <li key={i}>
                                    <CheckIcon />
                                    <span>{feature}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <button 
                    className="pricing-cta"
                    onClick={onOpenModal}
                >
                    {t('home.pricing.cta')}
                    <ArrowRightIcon />
                </button>
            </div>
        </section>
    );
}

// =============================================================================
// FAQ SECTION (UPDATED)
// =============================================================================

export function FAQSection() {
    const { t } = useTranslation();
    
    const faqs = [
        { q: t('home.faq.q1'), a: t('home.faq.a1') },
        { q: t('home.faq.q2'), a: t('home.faq.a2') },
        { q: t('home.faq.q3'), a: t('home.faq.a3') },
        { q: t('home.faq.q4'), a: t('home.faq.a4') },
    ];

    return (
        <section id="faq-section" className="faq-section section-container">
            <div className="section-inner">
                <h2 className="section-title">{t('home.faq.title')}</h2>
                <div className="faq-list">
                    {faqs.map((faq, i) => (
                        <details key={i} className="faq-item">
                            <summary>{faq.q}</summary>
                            <div className="faq-answer">{faq.a}</div>
                        </details>
                    ))}
                </div>
            </div>
        </section>
    );
}

// =============================================================================
// FOOTER CTA SECTION
// =============================================================================

interface FooterCTASectionProps {
    onScrollToForm: () => void;
}

export function FooterCTASection({ onScrollToForm }: FooterCTASectionProps) {
    const { t } = useTranslation();

    return (
        <section className="footer-cta-section">
            <div className="section-inner">
                <h2 className="footer-cta-title">{t('home.footerCta.title')}</h2>
                <button 
                    className="footer-cta-button"
                    onClick={onScrollToForm}
                >
                    {t('home.footerCta.button')}
                </button>
            </div>
        </section>
    );
}

// =============================================================================
// LEGACY COMPONENTS (kept for backward compatibility)
// =============================================================================

export function FeaturedDestinations() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const destinations = [
        { title: t('home.dest_italy'), image: '/images/photo-1523906834658-6e24ef2386f9.jpg', id: 'hidden-gems-italy' },
        { title: t('home.dest_bali'), image: '/images/photo-1537996194471-e657df975ab4.jpg', id: 'bali-spiritual-journey' },
        { title: t('home.dest_portugal'), image: '/images/photo-1555881400-74d7acaacd8b.jpg', id: 'portugal-coastline' },
        { title: t('home.dest_berlin'), image: '/images/photo-1467269204594-9661b134dd2b.jpg', id: 'berlin-art-scene' },
        { title: t('home.dest_spain'), image: '/images/photo-1543722530-d2c3201371e7.jpg', id: 'spain-tapas-guide' },
        { title: t('home.dest_sustainable'), image: '/images/photo-1473341304170-971dccb5ac1e.jpg', id: 'sustainable-travel-2026' },
    ];

    return (
        <section className="featured-section section-container">
            <div className="section-inner">
                <h2 className="section-title">{t('home.featured_title')}</h2>
                <div className="destinations-grid">
                    {destinations.map(dest => (
                        <div key={dest.id} className="dest-card">
                            <div className="dest-image" style={{ backgroundImage: `url(${dest.image})` }} />
                            <div className="dest-info">
                                <h3>{dest.title}</h3>
                                <button
                                    className="start-planning-btn"
                                    onClick={() => (navigate as any)({ to: `/blog/${dest.id}` })}
                                >
                                    {t('common.read_guide')} <span>→</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function ValueProps() {
    const { t } = useTranslation();
    const props = [
        {
            title: t('home.val_tailor_title'),
            desc: t('home.val_tailor_desc'),
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m12 19 7-7 3 3-7 7-3-3Z" /><path d="m18 13-1.5-7.5L2 2l3.5 14.5L13 18l5-5Z" /><path d="m2 2 8.8 8.8" /><path d="M9.2 15.6 18 13" />
                </svg>
            )
        },
        {
            title: t('home.val_cheaper_title'),
            desc: t('home.val_cheaper_desc'),
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17" /><path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.8-2.8L14 15" /><path d="m11 11-2.6-3.4a2 2 0 1 1 3.2-2.3L15 11" /><path d="M6 12h1" /><path d="M22 17h1" />
                </svg>
            )
        },
        {
            title: t('home.val_hidden_title'),
            desc: t('home.val_hidden_desc'),
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 3h12l3 6-9 12-9-12z" /><path d="M11 3 8 9l3 12" /><path d="M13 3l3 6-3 12" /><path d="M6.5 3 12 9l5.5-6" /><path d="M3 9h18" />
                </svg>
            )
        },
        {
            title: t('home.val_surprises_title'),
            desc: t('home.val_surprises_desc'),
            icon: (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /><path d="m9 12 2 2 4-4" />
                </svg>
            )
        }
    ];

    return (
        <section className="value-props section-container">
            <div className="section-inner">
                <h2 className="section-title">{t('home.value_props_title')}</h2>
                <p className="section-subtitle">{t('home.value_props_subtitle')}</p>
                <div className="props-grid">
                    {props.map(p => (
                        <div key={p.title} className="prop-card">
                            <div className="prop-icon">{p.icon}</div>
                            <h3>{p.title}</h3>
                            <p>{p.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
